import { useQuery } from '@tanstack/react-query';
import { Download, Trophy } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Matchdata } from '@/types/match';

import { fetchDemo } from '../api/getDemo';
import { generateDemoDownloadLink } from '../utils';

type Props = {
  match: Matchdata;
};

const Maps = (props: Props) => {
  const { data: demo } = useQuery({
    queryKey: ['demo', props.match.id],
    queryFn: () => fetchDemo(props.match.id.toString()),
  });

  return (
    <div className="bg-block flex justify-between rounded-lg border border-border px-8 py-12">
      <div className="relative flex items-center gap-x-6">
        <div className="relative grid aspect-video h-32 w-56 place-items-center overflow-hidden rounded-lg bg-background text-2xl font-semibold">
          {props.match.map1_name ? (
            <img
              src={`/images/${props.match.map1_name}.jpg`}
              className="h-full object-cover"
              alt=""
            />
          ) : (
            'Map 1'
          )}
          <div className="absolute inset-0 z-10 flex items-center justify-center text-5xl font-semibold">
            <span className="mr-1">
              {props.match.get5StatsMatch.get5StatsMaps[0].team1_score}
            </span>
            <span className="mb-2">:</span>
            <span className="ml-1">
              {props.match.get5StatsMatch.get5StatsMaps[0].team2_score}
            </span>
          </div>
          <div className="absolute inset-0 bg-[#102453] opacity-60"></div>
        </div>
        {props.match.get5StatsMatch.get5StatsMaps[0] && (
          <div className="flex w-48 flex-col gap-y-0.5 whitespace-nowrap text-lg font-semibold">
            <span className="flex gap-x-1 text-xs font-medium text-muted-foreground">
              <Trophy />
              Winner
            </span>
            {props.match.get5StatsMatch.get5StatsMaps[0].winner == 'team1' &&
              props.match.team1.team_name}
            {props.match.get5StatsMatch.get5StatsMaps[0].winner == 'team2' &&
              props.match.team2.team_name}
            {!props.match.get5StatsMatch.get5StatsMaps[0].winner && 'TBD'}
            <a
              href={demo && generateDemoDownloadLink(demo, props.match, 0)}
              target="_blank"
              className="absolute bottom-0 w-full"
              rel="noreferrer"
            >
              <Button
                variant="secondary"
                className="mt-1 whitespace-nowrap"
                disabled={!demo}
              >
                <Download />
                Download demo
              </Button>
            </a>
          </div>
        )}
      </div>
      <div className="relative flex items-center gap-x-6">
        <div className="relative grid aspect-video h-32 w-56 place-items-center overflow-hidden rounded-lg bg-background text-2xl font-semibold">
          {props.match.map2_name ? (
            <img
              src={`/images/${props.match.map2_name}.jpg`}
              className="h-full object-cover"
              alt=""
            />
          ) : (
            'Map 2'
          )}
          <div className="absolute inset-0 z-10 flex items-center justify-center text-5xl font-semibold">
            <span className="mr-1">
              {props.match.get5StatsMatch.get5StatsMaps[1].team1_score}
            </span>
            <span className="mb-2">:</span>
            <span className="ml-1">
              {props.match.get5StatsMatch.get5StatsMaps[1].team2_score}
            </span>
          </div>
          <div className="absolute inset-0 bg-[#102453] opacity-20"></div>
        </div>
        {props.match.get5StatsMatch.get5StatsMaps[1] && (
          <div className="flex w-48 flex-col gap-y-0.5 text-lg font-semibold">
            <span className="flex gap-x-1 text-xs font-medium text-muted-foreground">
              <Trophy />
              Winner
            </span>
            {props.match.get5StatsMatch.get5StatsMaps[1].winner == 'team1' &&
              props.match.team1.team_name}
            {props.match.get5StatsMatch.get5StatsMaps[1].winner == 'team2' &&
              props.match.team2.team_name}
            {!props.match.get5StatsMatch.get5StatsMaps[1].winner && 'TBD'}
            <a
              href={demo && generateDemoDownloadLink(demo, props.match, 1)}
              target="_blank"
              className="absolute bottom-0 w-full"
              rel="noreferrer"
            >
              <Button
                variant="secondary"
                className="mt-1 whitespace-nowrap"
                disabled={!demo}
              >
                <Download />
                Download demo
              </Button>
            </a>
          </div>
        )}
      </div>
      <div className="relative flex items-center gap-x-6">
        <div className="relative grid aspect-video h-32 w-56 place-items-center overflow-hidden rounded-lg bg-background text-2xl font-semibold">
          {props.match.map3_name ? (
            <img
              src={`/images/${props.match.map3_name}.jpg`}
              className="h-full object-cover"
              alt=""
            />
          ) : (
            'Map 3'
          )}
          <div className="absolute inset-0 z-10 flex items-center justify-center text-5xl font-semibold">
            <span className="mr-1">
              {props.match.get5StatsMatch.get5StatsMaps[2]?.team1_score}
            </span>
            <span className="mb-2">
              {props.match.get5StatsMatch.get5StatsMaps[2] ? ':' : ''}
            </span>
            <span className="ml-1">
              {props.match.get5StatsMatch.get5StatsMaps[2]?.team2_score}
            </span>
          </div>
          <div className="absolute inset-0 bg-[#102453] opacity-60"></div>
        </div>
        {props.match.get5StatsMatch.get5StatsMaps[2] && (
          <div className="flex w-48 flex-col gap-y-0.5 text-lg font-semibold">
            <span className="flex gap-x-1 text-xs font-medium text-muted-foreground">
              <Trophy />
              Winner
            </span>
            {props.match.get5StatsMatch.get5StatsMaps[2].winner == 'team1' &&
              props.match.team1.team_name}
            {props.match.get5StatsMatch.get5StatsMaps[2].winner == 'team2' &&
              props.match.team2.team_name}
            {!props.match.get5StatsMatch.get5StatsMaps[2].winner && 'TBD'}
            <a
              href={demo && generateDemoDownloadLink(demo, props.match, 2)}
              target="_blank"
              className="absolute bottom-0 w-full"
              rel="noreferrer"
            >
              <Button
                variant="secondary"
                className="mt-1 whitespace-nowrap"
                disabled={!demo}
              >
                <Download />
                Download demo
              </Button>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default Maps;
